import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";

import { environmentConfig } from "../environment/config";
import { getIsEmbedded } from "./getIsEmbedded";

// Store all Cognito-related localStorage entries as a map
export interface EmbeddedAuthData {
  cognitoStorage: Record<string, string>;
  timestamp: number;
}

interface StoredTokenMessage {
  type: "storedToken";
  authData?: EmbeddedAuthData;
}

function isStoredTokenMessage(data: unknown): data is StoredTokenMessage {
  if (typeof data !== "object" || data === null) {
    return false;
  }
  const message = data as Record<string, unknown>;
  return message.type === "storedToken";
}

function isValidCognitoStorage(storage: unknown): storage is Record<string, string> {
  if (typeof storage !== "object" || storage === null) {
    return false;
  }
  return Object.entries(storage).every(
    ([key, value]) => typeof key === "string" && typeof value === "string"
  );
}

// Collect all Cognito-related localStorage keys
function collectCognitoStorage(): Record<string, string> {
  const storage: Record<string, string> = {};

  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index);
    if (key?.includes("CognitoIdentityServiceProvider")) {
      const value = localStorage.getItem(key);
      if (value) {
        storage[key] = value;
      }
    }
  }

  return storage;
}

// Restore Cognito localStorage entries
function restoreCognitoStorage(storage: Record<string, string>): void {
  for (const [key, value] of Object.entries(storage)) {
    localStorage.setItem(key, value);
  }
}

function hasParentWindow(): boolean {
  return (
    typeof globalThis !== "undefined" &&
    globalThis.parent !== undefined &&
    globalThis.parent !== (globalThis as unknown as Window)
  );
}

function buildAllowedHiringPortalOrigins(): string[] {
  const configOrigin = new URL(environmentConfig.REACT_APP_HIRING_PORTAL_URL).origin;
  const origins = [configOrigin];

  const { hostname, protocol } = new URL(configOrigin);
  if (hostname.startsWith("www.")) {
    origins.push(`${protocol}//${hostname.replace(/^www\./, "")}`);
  } else if (hostname.split(".").length === 2) {
    origins.push(`${protocol}//www.${hostname}`);
  }

  return origins;
}

function getParentOriginFromReferrer(allowedOrigins: string[]): string | undefined {
  if (!hasParentWindow() || !document.referrer) {
    return undefined;
  }

  try {
    const referrerOrigin = new URL(document.referrer).origin;
    if (allowedOrigins.includes(referrerOrigin)) {
      return referrerOrigin;
    }
  } catch {
    return undefined;
  }

  return undefined;
}

/**
 * Posts a message to the parent window. When the exact parent origin is known (via referrer),
 * posts to that origin. Otherwise, posts to all allowed origins as a fallback — postMessage
 * with a non-matching targetOrigin silently fails, so this is safe.
 */
function postMessageToParent(message: unknown, allowedOrigins: string[]): void {
  const parentOrigin = getParentOriginFromReferrer(allowedOrigins);

  if (parentOrigin) {
    globalThis.parent.postMessage(message, parentOrigin);
    return;
  }

  // Referrer unavailable — try all allowed origins (only the matching one will deliver)
  for (const origin of allowedOrigins) {
    globalThis.parent.postMessage(message, origin);
  }
}

// Module-level state for the promise-based token restoration
let tokenRestorationPromise: Promise<void> | undefined;

/**
 * Waits for token restoration from parent window to complete.
 * This should be called BEFORE checking Cognito auth state when in embedded mode.
 *
 * - If not embedded, resolves immediately
 * - If tokens exist in localStorage, resolves immediately (Browser hasn't cleared them yet)
 * - If embedded, requests tokens from parent and waits for response (max 1 second timeout)
 *
 * Safe to call multiple times - uses a singleton promise.
 */
export async function waitForTokenRestoration(): Promise<void> {
  // Not embedded - no need to restore tokens
  if (!getIsEmbedded()) {
    return;
  }

  // Check if we already have Cognito tokens in localStorage
  // If so, Browser hasn't cleared them yet - no need to request from parent
  const existingStorage = collectCognitoStorage();
  if (Object.keys(existingStorage).length > 0) {
    logEvent(APP_V2_APP_EVENTS.EMBEDDED_TOKEN_RESTORATION_SKIPPED_TOKENS_EXIST, {
      tokenCount: Object.keys(existingStorage).length,
    });
    return;
  }

  // Wait for existing promise if already in progress
  if (tokenRestorationPromise) {
    await tokenRestorationPromise;
    return;
  }

  logEvent(APP_V2_APP_EVENTS.EMBEDDED_TOKEN_RESTORATION_STARTED);

  const allowedOrigins = buildAllowedHiringPortalOrigins();

  // Create the restoration promise
  tokenRestorationPromise = new Promise<void>((resolve) => {
    const TIMEOUT_MS = 1000;
    let resolved = false;

    const cleanup = () => {
      globalThis.removeEventListener("message", handleMessage);
    };

    const handleResolve = (reason: "success" | "timeout") => {
      if (!resolved) {
        resolved = true;
        cleanup();

        if (reason === "timeout") {
          logEvent(APP_V2_APP_EVENTS.EMBEDDED_TOKEN_RESTORATION_TIMEOUT);
          // Reset promise on timeout so future calls can retry
          tokenRestorationPromise = undefined;
        }

        resolve();
      }
    };

    function handleMessage(event: MessageEvent) {
      // Verify message origin matches expected parent (hiring portal)
      if (!allowedOrigins.includes(event.origin)) {
        return;
      }

      if (!isStoredTokenMessage(event.data)) {
        return;
      }

      const { authData } = event.data;

      // Restore Cognito storage if we have valid data
      if (
        authData?.cognitoStorage &&
        isValidCognitoStorage(authData.cognitoStorage) &&
        Object.keys(authData.cognitoStorage).length > 0
      ) {
        restoreCognitoStorage(authData.cognitoStorage);
        logEvent(APP_V2_APP_EVENTS.EMBEDDED_TOKEN_RESTORATION_SUCCESS, {
          tokenCount: Object.keys(authData.cognitoStorage).length,
        });
      } else {
        logEvent(APP_V2_APP_EVENTS.EMBEDDED_TOKEN_RESTORATION_SUCCESS, {
          tokenCount: 0,
        });
      }

      handleResolve("success");
    }

    // Listen for response from parent
    globalThis.addEventListener("message", handleMessage);

    // Request tokens from parent (only if we're in an iframe)
    if (hasParentWindow()) {
      postMessageToParent({ type: "requestStoredToken" }, allowedOrigins);
    }

    // Timeout fallback - don't wait forever if parent doesn't respond
    setTimeout(() => {
      handleResolve("timeout");
    }, TIMEOUT_MS);
  });

  await tokenRestorationPromise;
}

// Standalone functions for use outside of React components

/**
 * Stores current Cognito auth tokens in parent window for Browser / Safari ITP workaround.
 * Call this after successful authentication.
 */
export function storeEmbeddedAuth(): void {
  if (!getIsEmbedded() || !hasParentWindow()) {
    return;
  }

  const cognitoStorage = collectCognitoStorage();
  if (Object.keys(cognitoStorage).length === 0) {
    return;
  }

  const authData: EmbeddedAuthData = {
    cognitoStorage,
    timestamp: Date.now(),
  };

  const allowedOrigins = buildAllowedHiringPortalOrigins();
  postMessageToParent({ type: "storeToken", authData }, allowedOrigins);

  logEvent(APP_V2_APP_EVENTS.EMBEDDED_TOKEN_STORE_SENT, {
    tokenCount: Object.keys(cognitoStorage).length,
  });
}

/**
 * Clears auth tokens from parent window.
 * This supports the clipboardhiring.com hiring portal embed feature.
 * Call this on logout.
 */
export function clearEmbeddedAuth(): void {
  if (!getIsEmbedded() || !hasParentWindow()) {
    return;
  }

  logEvent(APP_V2_APP_EVENTS.EMBEDDED_TOKEN_CLEAR_SENT);
  const allowedOrigins = buildAllowedHiringPortalOrigins();
  postMessageToParent({ type: "clearToken" }, allowedOrigins);
}
